import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { AnmeldeKonfig } from '../../shared/rollen';
import { api } from '../api';

// Google Identity Services (Knopf „Mit Google anmelden“); nur die Teile, die hier gebraucht werden
interface GoogleId {
  initialize(o: { client_id: string; callback: (r: { credential: string }) => void; hd?: string; ux_mode?: 'popup'; auto_select?: boolean }): void;
  renderButton(el: HTMLElement, o: Record<string, string | number>): void;
}
declare global { interface Window { google?: { accounts: { id: GoogleId } } } }

function gsiLaden(): Promise<GoogleId> {
  return new Promise((ok, fehler) => {
    if (window.google) return ok(window.google.accounts.id);
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => (window.google ? ok(window.google.accounts.id) : fehler(new Error('Google-Anmeldung nicht geladen.')));
    s.onerror = () => fehler(new Error('Google-Anmeldung nicht erreichbar. Internetverbindung prüfen.'));
    document.head.append(s);
  });
}

/** Anmeldung ins Teamportal nach der Marke (Nacht, ein Lime-Knopf): mit Google für Neuratek-Konten, sonst mit Mail und Passwort */
export function Anmeldung({ angemeldet }: { angemeldet: () => void }) {
  const [konfig, setKonfig] = useState<AnmeldeKonfig | null>(null);
  const [fehler, setFehler] = useState('');
  const [mail, setMail] = useState('');
  const [passwort, setPasswort] = useState('');
  // Startpasswort: erst ein eigenes festlegen
  const [aendern, setAendern] = useState(false);
  const [neu, setNeu] = useState('');
  const [neu2, setNeu2] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const knopf = useRef<HTMLDivElement>(null);

  useEffect(() => { api<AnmeldeKonfig>('/api/anmeldung/konfig').then(setKonfig, e => setFehler((e as Error).message)); }, []);

  useEffect(() => {
    const clientId = konfig?.googleClientId;
    if (!clientId || !knopf.current) return;
    const el = knopf.current;
    gsiLaden().then(gsi => {
      gsi.initialize({
        client_id: clientId, hd: 'neuratek.de', ux_mode: 'popup',
        callback: r => { setFehler(''); api('/api/anmeldung/google', { methode: 'POST', daten: { credential: r.credential } }).then(angemeldet, e => setFehler((e as Error).message)); }
      });
      gsi.renderButton(el, { theme: 'filled_black', size: 'large', text: 'signin_with', shape: 'pill', locale: 'de', logo_alignment: 'left', width: Math.min(400, Math.max(200, el.clientWidth)) });
    }, e => setFehler((e as Error).message));
  }, [konfig, angemeldet]);

  const anmelden = async (e: FormEvent) => {
    e.preventDefault();
    if (aendern && neu !== neu2) { setFehler('Die beiden neuen Passwörter sind nicht gleich.'); return; }
    setFehler(''); setLaeuft(true);
    try {
      const r = await api<{ ok?: boolean; aendern?: boolean }>('/api/anmeldung/passwort', { methode: 'POST', daten: { email: mail.trim(), passwort, ...(aendern ? { neu } : {}) } });
      if (r.aendern) { setAendern(true); setLaeuft(false); return; }
      angemeldet();
    } catch (f) { setFehler((f as Error).message); setLaeuft(false); }
  };

  const pfeil = <span className="anm-pfeil" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></span>;

  return (
    <main className="anm">
      <div className="anm-inhalt">
        <div className="anm-marke"><img src="/logo-zeichen.png" alt="" width={26} height={28} /><span>Neuratek</span></div>
        <div className="anm-kopf">
          <h1>Willkommen im <em>Teamportal</em></h1>
          <p>Melde dich an, um weiterzuarbeiten.</p>
          {konfig && konfig.umgebung !== 'echt' && <span className="anm-pille">Testumgebung · erfundene Daten</span>}
        </div>

        {!aendern && <><section className="anm-weg" aria-labelledby="h-google">
          <h2 id="h-google">Mit Google anmelden</h2>
          <p className="anm-hilfe">Für alle mit einem Google-Konto von Neuratek.</p>
          {konfig && !konfig.googleClientId ? <p className="anm-hilfe">Die Anmeldung mit Google wird gerade eingerichtet.</p> : <div className="anm-gsi" ref={knopf} />}
        </section>
        <div className="anm-oder"><span>oder</span></div></>}

        <section className="anm-weg" aria-labelledby="h-pw">
          <h2 id="h-pw">{aendern ? 'Eigenes Passwort festlegen' : 'Mail und Passwort'}</h2>
          <form onSubmit={anmelden} className="anm-form">
            {aendern ? <>
              <p className="anm-hilfe" role="status">Du hast dich mit dem Startpasswort angemeldet. Lege jetzt ein eigenes fest, mindestens 12 Zeichen.</p>
              <label className="anm-feld"><span>Neues Passwort</span><input type="password" required minLength={12} autoComplete="new-password" autoFocus value={neu} onChange={e => setNeu(e.target.value)} /></label>
              <label className="anm-feld"><span>Neues Passwort wiederholen</span><input type="password" required minLength={12} autoComplete="new-password" value={neu2} onChange={e => setNeu2(e.target.value)} /></label>
              <button className="anm-knopf" disabled={laeuft}>Speichern und anmelden{pfeil}</button>
            </> : <>
              <p className="anm-hilfe">Ohne Google-Konto, zum Beispiel als Opener: mit deiner Neuratek-Adresse und deinem Passwort.</p>
              <label className="anm-feld"><span>Mail-Adresse</span><input type="email" required autoComplete="username" value={mail} placeholder="nachname@neuratek.de" onChange={e => setMail(e.target.value)} /></label>
              <label className="anm-feld"><span>Passwort</span><input type="password" required autoComplete="current-password" value={passwort} onChange={e => setPasswort(e.target.value)} /></label>
              <button className="anm-knopf" disabled={laeuft}>Anmelden{pfeil}</button>
            </>}
          </form>
        </section>

        {fehler && <p className="anm-fehler" role="alert">{fehler}</p>}
        <p className="anm-fuss">Passwort vergessen oder Probleme beim Anmelden? Leon setzt es dir zurück.</p>
      </div>
    </main>
  );
}
